import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SyncIcon from '@mui/icons-material/Sync';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { Title, useNotify } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../utils/setupAxiosAuth';
import useAuthorize from '../hooks/useAuthorize';
import { INQUIRIES_MODULE_ROLES } from '../roles/portalRoles';
import { getAdminApiBaseUrl } from '../utils/adminApiBaseUrl';
import {
  fetchInquiryReplies,
  fetchBuyerWhatsAppSession,
  InquirySellerReply,
  BuyerWhatsAppSession,
  refreshInquiryRepliesFromInbox,
  sendBuyerWhatsAppMessage,
  syncInquiryRepliesFromPlivo,
} from './inquiryWorkflowApi';

const adminAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('user_authToken')}`,
});

const formatPrice = (value: number | null | undefined) =>
  value == null || Number.isNaN(Number(value)) ? '—' : `₹${Number(value).toLocaleString('en-IN')}`;

const formatDateTime = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleString('en-IN') : '—';

/** Collapse blank lines between timestamped history entries for a tighter log view. */
const compactCustomerNotes = (notes: string) =>
  String(notes || '')
    .replace(/\r\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

interface InquiryDetail {
  inquiry_date?: string;
  inquiry_received_at?: string;
  customer_contact?: string;
  customer_name?: string;
  company_name?: string;
  customer_pin?: string;
  customer_state?: string;
  phone_mobile?: string;
  product_name?: string;
  product_grade?: string;
  inquiry_city_name?: string;
  inquiry_quantity?: number;
  inquiry_price?: number;
  inquiry_total?: number;
  inquiry_status?: string;
  customer_notes?: string;
  customer_contacted?: boolean;
  customer_approved?: boolean | null;
}

const detailGridProps = { xs: 12, sm: 6, md: 3 } as const;

const MetaItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Typography variant="body2" sx={{ lineHeight: 1.35, fontSize: '0.8125rem' }}>
    <Box component="span" sx={{ fontWeight: 700 }}>
      {label}:
    </Box>{' '}
    {value ?? '—'}
  </Typography>
);

const statusLabel = (row: InquirySellerReply) => {
  if (row.message_count && row.message_count > 0) {
    return row.parse_status || 'parsed';
  }
  if (row.broadcast_status === 'failed') return 'send failed';
  return 'awaiting reply';
};

const parseInquiryReceivedAt = (detail: InquiryDetail | null): Date | null => {
  if (!detail) return null;
  if (detail.inquiry_received_at) {
    const parsed = new Date(detail.inquiry_received_at);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  if (detail.inquiry_date) {
    const match = detail.inquiry_date.match(/^(\d{2})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})$/);
    if (match) {
      const [, dd, mm, yy, hh, min] = match;
      return new Date(
        2000 + parseInt(yy, 10),
        parseInt(mm, 10) - 1,
        parseInt(dd, 10),
        parseInt(hh, 10),
        parseInt(min, 10)
      );
    }
  }
  return null;
};

const formatElapsed = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  if (days > 0 || hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  return parts.join(', ');
};

const useElapsedLabel = (since: Date | null) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!since) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, [since]);

  if (!since) return null;
  return formatElapsed(now - since.getTime());
};

const InquiryReplies = () => {
  useAuthorize(INQUIRIES_MODULE_ROLES);
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const id = parseInt(inquiryId || '0', 10);

  const [rows, setRows] = useState<InquirySellerReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [customerNotes, setCustomerNotes] = useState('');
  const [customerContacted, setCustomerContacted] = useState(false);
  const [inquiryDetail, setInquiryDetail] = useState<InquiryDetail | null>(null);
  const [notesLoading, setNotesLoading] = useState(true);
  const [notesSaving, setNotesSaving] = useState(false);
  const [buyerWhatsAppMessage, setBuyerWhatsAppMessage] = useState('');
  const [buyerWhatsAppSending, setBuyerWhatsAppSending] = useState(false);
  const [buyerWhatsAppSession, setBuyerWhatsAppSession] = useState<BuyerWhatsAppSession | null>(null);
  const refreshInFlight = useRef(false);
  const syncInFlight = useRef(false);
  const notesDirtyRef = useRef(false);
  const notesFocusedRef = useRef(false);
  const lastServerNotesRef = useRef('');

  const applyServerCustomerNotes = useCallback((notes: string, contacted: boolean) => {
    const compacted = compactCustomerNotes(notes);
    lastServerNotesRef.current = compacted;
    if (!notesDirtyRef.current && !notesFocusedRef.current) {
      setCustomerNotes(compacted);
      setCustomerContacted(contacted);
    }
  }, []);

  const replaceCustomerNotesFromServer = useCallback((notes: string, contacted: boolean) => {
    notesDirtyRef.current = false;
    const compacted = compactCustomerNotes(notes);
    lastServerNotesRef.current = compacted;
    setCustomerNotes(compacted);
    setCustomerContacted(contacted);
  }, []);

  const loadCustomerNotes = useCallback(async () => {
    if (!id) return;
    setNotesLoading(true);
    try {
      const response = await axios.get(`${getAdminApiBaseUrl()}/inquiries/${id}`, {
        headers: adminAuthHeaders(),
      });
      const data = response.data || {};
      const notes = compactCustomerNotes(data.customer_notes || '');
      setInquiryDetail(data);
      notesDirtyRef.current = false;
      lastServerNotesRef.current = notes;
      setCustomerNotes(notes);
      setCustomerContacted(Boolean(data.customer_contacted));
    } catch (error) {
      console.error(error);
      notify('Failed to load customer conversation notes', { type: 'error' });
    } finally {
      setNotesLoading(false);
    }
  }, [id, notify]);

  const saveCustomerNotes = useCallback(async () => {
    if (!id) return;
    setNotesSaving(true);
    try {
      const response = await axios.put(
        `${getAdminApiBaseUrl()}/inquiries/${id}/customer-contact`,
        { customer_notes: customerNotes },
        { headers: adminAuthHeaders() }
      );
      const savedNotes = response.data?.customer_notes || '';
      replaceCustomerNotesFromServer(savedNotes, Boolean(response.data?.customer_contacted));
      notify(
        response.data?.customer_contacted
          ? 'Customer notes saved — Customer Contact set to Yes'
          : 'Customer notes cleared — Customer Contact set to No',
        { type: 'info' }
      );
    } catch (error) {
      console.error(error);
      notify('Failed to save customer notes', { type: 'error' });
    } finally {
      setNotesSaving(false);
    }
  }, [customerNotes, id, notify, replaceCustomerNotesFromServer]);

  const loadBuyerWhatsAppSession = useCallback(async () => {
    if (!id) return;
    try {
      const session = await fetchBuyerWhatsAppSession(id);
      setBuyerWhatsAppSession(session);
    } catch (error) {
      console.error(error);
      setBuyerWhatsAppSession({
        can_send: false,
        can_send_template: false,
        can_send_any: false,
        reason: 'Unable to check WhatsApp session status',
      });
    }
  }, [id]);

  const sendBuyerWhatsApp = useCallback(async () => {
    if (!id) return;
    const message = buyerWhatsAppMessage.trim();
    if (!message) {
      notify('Enter a message to send to the buyer', { type: 'warning' });
      return;
    }
    setBuyerWhatsAppSending(true);
    try {
      const result = await sendBuyerWhatsAppMessage(id, message);
      if (result.customer_notes != null) {
        replaceCustomerNotesFromServer(result.customer_notes, Boolean(result.customer_contacted));
      }
      setBuyerWhatsAppMessage('');
      await loadBuyerWhatsAppSession();
      notify('WhatsApp message sent to buyer', { type: 'success' });
    } catch (error: unknown) {
      console.error(error);
      const apiError = error as { response?: { data?: { error?: string } } };
      notify(apiError.response?.data?.error || 'Failed to send WhatsApp to buyer', { type: 'error' });
    } finally {
      setBuyerWhatsAppSending(false);
    }
  }, [buyerWhatsAppMessage, id, loadBuyerWhatsAppSession, notify, replaceCustomerNotesFromServer]);

  const load = useCallback(
    async (opts: { quiet?: boolean } = {}) => {
      if (!id) return;
      if (!opts.quiet) {
        setLoading(true);
      }
      try {
        const replyRows = await fetchInquiryReplies(id);
        setRows(replyRows);
        setLastUpdatedAt(new Date());
      } catch (error) {
        console.error(error);
        if (!opts.quiet) {
          notify('Failed to load inquiry seller responses', { type: 'error' });
        }
      } finally {
        if (!opts.quiet) {
          setLoading(false);
        }
      }
    },
    [id, notify]
  );

  const refreshFromInbox = useCallback(async () => {
    if (!id || refreshInFlight.current) return;
    refreshInFlight.current = true;
    try {
      const result = await refreshInquiryRepliesFromInbox(id);
      if (result.replies) {
        setRows(result.replies);
        setLastUpdatedAt(new Date());
      }
      if (result.customer_notes !== undefined) {
        applyServerCustomerNotes(result.customer_notes || '', Boolean(result.customer_contacted));
        setInquiryDetail((prev) =>
          prev
            ? {
                ...prev,
                customer_notes: result.customer_notes || '',
                customer_contacted: Boolean(result.customer_contacted),
                customer_approved: result.customer_approved ?? prev.customer_approved,
              }
            : prev
        );
      }
      if (result.customer_approved === true) {
        try {
          const response = await axios.get(`${getAdminApiBaseUrl()}/inquiries/${id}`, {
            headers: adminAuthHeaders(),
          });
          setInquiryDetail(response.data || null);
        } catch {
          // keep patched detail
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      refreshInFlight.current = false;
    }
  }, [applyServerCustomerNotes, id]);

  const syncFromPlivo = useCallback(
    async (opts: { quiet?: boolean; syncDays?: number } = {}) => {
      if (!id || syncInFlight.current) return;
      syncInFlight.current = true;
      if (!opts.quiet) {
        setSyncing(true);
      }
      try {
        const result = await syncInquiryRepliesFromPlivo(id, {
          runProviderSync: true,
          syncDays: opts.syncDays ?? 1,
        });
        setRows(result.replies || []);
        setLastUpdatedAt(new Date());
        if (!opts.quiet) {
          notify(
            result.new_messages
              ? `Processed ${result.new_messages} new message(s); updated ${result.synced} seller(s)`
              : 'No new seller replies since last sync',
            { type: result.synced ? 'success' : 'info' }
          );
        }
      } catch (error) {
        console.error(error);
        if (!opts.quiet) {
          notify('Failed to sync from Plivo', { type: 'error' });
        }
      } finally {
        syncInFlight.current = false;
        if (!opts.quiet) {
          setSyncing(false);
        }
      }
    },
    [id, notify]
  );

  useEffect(() => {
    const init = async () => {
      await Promise.all([loadCustomerNotes(), load()]);
      await refreshFromInbox();
      await loadBuyerWhatsAppSession();
    };
    init();
  }, [load, loadBuyerWhatsAppSession, loadCustomerNotes, refreshFromInbox]);

  useEffect(() => {
    if (!id) return undefined;
    const POLL_MS = 20000;
    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      refreshFromInbox();
      loadBuyerWhatsAppSession();
    };
    const timer = window.setInterval(tick, POLL_MS);
    const onFocus = () => {
      refreshFromInbox();
      loadBuyerWhatsAppSession();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [id, loadBuyerWhatsAppSession, refreshFromInbox]);

  useEffect(() => {
    if (!id) return undefined;
    const BACKGROUND_SYNC_MS = 2 * 60 * 1000;
    const tick = () => {
      if (document.visibilityState === 'hidden' || syncing) return;
      syncFromPlivo({ quiet: true, syncDays: 1 });
    };
    const first = window.setTimeout(tick, 20000);
    const timer = window.setInterval(tick, BACKGROUND_SYNC_MS);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
  }, [id, syncFromPlivo, syncing]);

  const handleSync = async () => {
    if (!id) return;
    await syncFromPlivo({ quiet: false, syncDays: 2 });
  };

  const repliedCount = rows.filter((row) => (row.message_count || 0) > 0).length;
  const buyerPhoneAvailable =
    buyerWhatsAppSession?.buyer_phone ||
    inquiryDetail?.phone_mobile ||
    inquiryDetail?.customer_contact ||
    null;
  const buyerWhatsAppDisabled = !buyerPhoneAvailable || buyerWhatsAppSending;
  const buyerWhatsAppUsesTemplate =
    Boolean(buyerPhoneAvailable) &&
    (buyerWhatsAppSession?.delivery_mode === 'template' ||
      (buyerWhatsAppSession && !buyerWhatsAppSession.can_send));
  const buyerSessionExpiresLabel = buyerWhatsAppSession?.session_expires_at
    ? new Date(buyerWhatsAppSession.session_expires_at).toLocaleString('en-IN')
    : null;
  const inquiryReceivedAt = parseInquiryReceivedAt(inquiryDetail);
  const elapsedLabel = useElapsedLabel(inquiryReceivedAt);
  const buyerResponsePending = Boolean(buyerWhatsAppSession?.response_pending);
  const buyerResponsePendingAt = buyerWhatsAppSession?.last_inbound_at
    ? formatDateTime(buyerWhatsAppSession.last_inbound_at)
    : null;

  return (
    <Box sx={{ px: 2, pt: 1, pb: 2 }}>
      <Title title={`Inquiry #${id} Replies`} />
      {buyerResponsePending && (
        <Alert severity="error" sx={{ mb: 1.5, fontWeight: 600 }}>
          Response pending — the buyer sent a WhatsApp message
          {buyerResponsePendingAt ? ` on ${buyerResponsePendingAt}` : ''}. Credco needs to reply.
          {buyerWhatsAppSession?.last_inbound_preview
            ? ` Latest: “${buyerWhatsAppSession.last_inbound_preview}”`
            : ''}
        </Alert>
      )}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/inquiries')}
        size="small"
        sx={{ mb: 1 }}
      >
        Back to inquiries
      </Button>

      <Paper sx={{ px: 1.5, py: 1, mb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 0.75,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Inquiry ID {id} details
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {buyerResponsePending && (
              <Chip
                size="small"
                label="Response pending"
                color="error"
                variant="filled"
                sx={{ fontWeight: 700 }}
              />
            )}
            {elapsedLabel && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap', lineHeight: 2.2 }}
              >
                {elapsedLabel} running
              </Typography>
            )}
          </Box>
        </Box>
        {notesLoading && !inquiryDetail ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            Loading inquiry…
          </Typography>
        ) : (
          <Grid container spacing={0.5}>
            <Grid item {...detailGridProps}>
              <MetaItem label="Customer" value={inquiryDetail?.customer_contact || inquiryDetail?.phone_mobile} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="Customer name" value={inquiryDetail?.customer_name} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="Company name" value={inquiryDetail?.company_name} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="PIN" value={inquiryDetail?.customer_pin} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="State" value={inquiryDetail?.customer_state} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="Product" value={inquiryDetail?.product_name} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="Grade" value={inquiryDetail?.product_grade || '—'} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="Delivery city" value={inquiryDetail?.inquiry_city_name} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem
                label="Quantity"
                value={
                  inquiryDetail?.inquiry_quantity != null
                    ? `${inquiryDetail.inquiry_quantity} MT`
                    : '—'
                }
              />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="Inquiry price" value={`${formatPrice(inquiryDetail?.inquiry_price)} / MT`} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="Inquiry total" value={formatPrice(inquiryDetail?.inquiry_total)} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="Inquiry date" value={inquiryDetail?.inquiry_date} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem label="Status" value={inquiryDetail?.inquiry_status} />
            </Grid>
            <Grid item {...detailGridProps}>
              <MetaItem
                label="Contact / Approval"
                value={
                  inquiryDetail
                    ? `${inquiryDetail.customer_contacted ? 'Yes' : 'No'} / ${
                        inquiryDetail.customer_approved === true
                          ? 'Yes'
                          : inquiryDetail.customer_approved === false
                            ? 'No'
                            : '—'
                      }`
                    : '—'
                }
              />
            </Grid>
          </Grid>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Customer conversation notes
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {buyerResponsePending && (
              <Chip
                size="small"
                label="Response pending: Yes"
                color="error"
                variant="filled"
                sx={{ fontWeight: 700 }}
              />
            )}
            <Chip
              size="small"
              label={customerContacted ? 'Customer Contact: Yes' : 'Customer Contact: No'}
              color={customerContacted ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
        </Box>
        <TextField
          value={customerNotes}
          onChange={(event) => {
            notesDirtyRef.current = true;
            setCustomerNotes(event.target.value);
          }}
          onFocus={() => {
            notesFocusedRef.current = true;
          }}
          onBlur={() => {
            notesFocusedRef.current = false;
          }}
          multiline
          minRows={6}
          fullWidth
          disabled={notesLoading || notesSaving}
          placeholder="Enter customer conversation notes..."
          sx={{
            mb: 1.5,
            '& .MuiInputBase-root': {
              fontFamily: 'inherit',
              alignItems: 'flex-start',
            },
            '& textarea': {
              maxHeight: 280,
              overflowY: 'auto !important',
              lineHeight: 1.35,
            },
          }}
        />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={saveCustomerNotes}
          disabled={notesLoading || notesSaving}
        >
          {notesSaving ? 'Saving...' : 'Save customer notes'}
        </Button>

        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          {!buyerWhatsAppDisabled && !buyerWhatsAppUsesTemplate && buyerSessionExpiresLabel && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8125rem' }}>
              Session open until {buyerSessionExpiresLabel} (free-text WhatsApp)
            </Typography>
          )}
          <TextField
            value={buyerWhatsAppMessage}
            onChange={(event) => setBuyerWhatsAppMessage(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            disabled={buyerWhatsAppSending || buyerWhatsAppDisabled}
            placeholder={
              buyerWhatsAppDisabled
                ? 'Buyer phone or WhatsApp template not available'
                : 'WhatsApp message for the buyer'
            }
            sx={{
              mb: 1.5,
              '& .MuiInputBase-root': {
                fontFamily: 'inherit',
                alignItems: 'flex-start',
              },
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SendIcon />}
            onClick={sendBuyerWhatsApp}
            disabled={buyerWhatsAppSending || buyerWhatsAppDisabled || !buyerWhatsAppMessage.trim()}
          >
            {buyerWhatsAppSending ? 'Sending...' : 'Send WhatsApp to the buyer'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing...' : 'Sync from Plivo'}
        </Button>
        <Chip
          label={`${repliedCount} / ${rows.length} sellers replied`}
          color="primary"
          variant="outlined"
        />
        {lastUpdatedAt && (
          <Typography variant="caption" color="text.secondary">
            Last updated {lastUpdatedAt.toLocaleTimeString('en-IN')}
          </Typography>
        )}
      </Paper>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Seller</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Listed price</TableCell>
              <TableCell>Listed price updated</TableCell>
              <TableCell>Quoted price</TableCell>
              <TableCell>Qty (MT)</TableCell>
              <TableCell>Available?</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Comments</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Msgs</TableCell>
              <TableCell>Last reply</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12}>Loading...</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12}>
                  No sellers notified yet for this inquiry.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={`${row.inquiry_id}-${row.seller_id}`}>
                  <TableCell>{row.seller_name || `#${row.seller_id}`}</TableCell>
                  <TableCell>{row.seller_phone || '—'}</TableCell>
                  <TableCell>{formatPrice(row.listed_price)}</TableCell>
                  <TableCell>{formatDateTime(row.listed_price_updated_at)}</TableCell>
                  <TableCell>{formatPrice(row.quoted_price)}</TableCell>
                  <TableCell>{row.quantity_available ?? '—'}</TableCell>
                  <TableCell>
                    {row.is_available == null ? '—' : row.is_available ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell>{row.location || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>
                    {row.comments || '—'}
                  </TableCell>
                  <TableCell>{statusLabel(row)}</TableCell>
                  <TableCell>{row.message_count ?? 0}</TableCell>
                  <TableCell>
                    {row.received_at ? new Date(row.received_at).toLocaleString('en-IN') : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default InquiryReplies;
